import { 
  OptimizedSelectedSuppliers,
  OptimizedSupplier,
  OptimizedItem,
  LegacyOptimizedSelectedSuppliers,
  OptimizedSalgadosSupplier,
  LegacySelectedSuppliers,
  LegacySalgadosItem 
} from '@/types/optimizedSupplierTypes';

/**
 * Formats monetary values to exactly 2 decimal places
 */
const formatMonetaryValue = (value: any): number => {
  if (value === null || value === undefined) return 0;
  const numValue = typeof value === 'number' ? value : parseFloat(String(value));
  return isNaN(numValue) ? 0 : parseFloat(numValue.toFixed(2));
};

/**
 * Formats monetary values to exactly 2 decimal places as string
 */
const formatMonetaryValueAsString = (value: any): string => {
  if (value === null || value === undefined) return "0.00";
  const numValue = typeof value === 'number' ? value : parseFloat(String(value));
  return isNaN(numValue) ? "0.00" : numValue.toFixed(2);
};


/**
 * Optimizes the selected_suppliers JSON - MAINTAINS LEGACY COMPATIBILITY
 * Returns structure compatible with existing database types
 */
export function optimizeSelectedSuppliers(data: any, guestCount?: number): LegacyOptimizedSelectedSuppliers {
  if (!data) return {};

  const optimized: LegacyOptimizedSelectedSuppliers = {};

  Object.entries(data).forEach(([category, items]: [string, any]) => {
    if (category === 'salgados' && Array.isArray(items)) {
      // Group salgados by supplier (legacy format)
      const supplierMap = new Map<string, OptimizedSalgadosSupplier>();

      items.forEach((item: LegacySalgadosItem) => {
        // 🛡️ SUPPLIER_ID FALLBACK: Try multiple sources for supplier_id
        const supplierId = item.supplier?.id || item.supplier_id || (item.supplier as any)?.supplier_id;
        if (!supplierId) {
          console.warn(`⚠️ [SUPPLIER_ID] Missing supplier_id for salgados item in optimizer:`, item);
          return;
        }

        if (!supplierMap.has(supplierId)) {
          supplierMap.set(supplierId, {
            supplier_id: supplierId, // Renomeado de 'id' para 'supplier_id'
            name: item.supplier?.name || 'Fornecedor',
            logo_url: item.supplier?.logo_url,
            contact_email: item.supplier?.contact_email,
            contact_phone: item.supplier?.contact_phone,
            items: [],
            quantity: 0 // Novo campo para quantidade total
          });
        }

        const supplier = supplierMap.get(supplierId)!;
        supplier.items.push({
          id: item.id,
          name: item.name,
          totalPrice: formatMonetaryValue(item.totalPrice),
          pricePerPerson: formatMonetaryValue(item.pricePerPerson),
          image_url: item.image_url || item.image || item.imageUrl,
          description: item.description,
          additional_items: Array.isArray(item.additionalItems) 
            ? item.additionalItems.join(', ') 
            : item.additional_items,
          necessary_items: Array.isArray(item.necessaryItems)
            ? item.necessaryItems.join(', ')
            : item.necessary_items,
          quantity: item.quantity,
          basePrice: formatMonetaryValue(item.basePrice),
          variants: item.variants,
          selectedVariant: item.selectedVariant,
          specialPricing: item.specialPricing,
          specialPrice: item.specialPrice === null || item.specialPrice === undefined ? null : formatMonetaryValue(item.specialPrice),
          priceAdditionalHour: formatMonetaryValueAsString(item.priceAdditionalHour),
          event_hour_time: item.event_hour_time
        });
        
        // Atualizar quantidade total do fornecedor usando guest_count se disponível
        supplier.quantity = guestCount !== undefined ? guestCount : supplier.quantity + (item.quantity || 1);
      });

      optimized.salgados = Array.from(supplierMap.values());
    } else {
      // Other categories - standardize supplier_id
      if (Array.isArray(items)) {
        optimized[category] = items.map((item: any) => ({
          ...item,
          supplier_id: item.supplier_id || item.supplier?.id
        }));
      } else {
        optimized[category] = items;
      }
    }
  });

  return optimized;
}

/**
 * Converts to NEW optimized structure (supplier IDs as keys)
 */
export function convertToOptimizedStructure(data: any, guestCount?: number): OptimizedSelectedSuppliers {
  if (!data) return {};

  const supplierMap = new Map<string, OptimizedSupplier>();

  Object.entries(data).forEach(([category, items]: [string, any]) => {
    
    // Handle special case for salgados that comes as array of suppliers or items
    if (category === 'salgados' && Array.isArray(items) && items.length > 0) {
      // Check if this is already optimized format (array of suppliers with items)
      const firstItem = items[0];
      if (firstItem && firstItem.items && Array.isArray(firstItem.items)) {
        items.forEach((supplierData: any) => {
          const supplierId = supplierData.id || supplierData.supplier_id;
          const supplierName = supplierData.name;
          
          if (!supplierId) return;
          
          // Add supplier to map
          if (!supplierMap.has(supplierId)) {
            supplierMap.set(supplierId, {
              id: supplierId,
              name: supplierName,
              logo_url: supplierData.logo_url,
              contact_email: supplierData.contact_email,
              contact_phone: supplierData.contact_phone,
              items: []
            });
          }
          
          const supplier = supplierMap.get(supplierId)!;
          
          // Process supplier's items
          supplierData.items.forEach((item: any) => {
            const optimizedItem: OptimizedItem = {
              id: item.id,
              name: item.name || item.product_name || item.product || 'Item sem nome',
              totalPrice: formatMonetaryValue(item.totalPrice || item.price || 0),
              category: 'salgados', // Force category to salgados
              quantity: item.quantity || 1,
              image_url: item.image_url || item.image || item.imageUrl,
              description: item.description,
              basePrice: formatMonetaryValue(item.basePrice || item.price || 0),
              pricePerPerson: formatMonetaryValue(item.pricePerPerson || 0),
              additional_items: Array.isArray(item.additionalItems) 
                ? item.additionalItems.join(', ') 
                : item.additional_items,
              necessary_items: Array.isArray(item.necessaryItems)
                ? item.necessaryItems.join(', ')
                : item.necessary_items,
              variants: item.variants,
              selectedVariant: item.selectedVariant,
              specialPricing: item.specialPricing,
              specialPrice: item.specialPrice === null || item.specialPrice === undefined ? null : formatMonetaryValue(item.specialPrice),
              priceAdditionalHour: formatMonetaryValueAsString(item.priceAdditionalHour),
              event_hour_time: item.event_hour_time,
            };
            
            // Preserve cake fields if this is a cake item
            const isCakeItem = item.type === 'bolo' || item.flavor || item.filling;
            if (isCakeItem) {
              optimizedItem.flavor = item.flavor;
              optimizedItem.colors = item.colors;
              optimizedItem.details = item.details;
              optimizedItem.filling = item.filling;
              optimizedItem.filling2 = item.filling2;
            }
            
            supplier.items.push(optimizedItem);
          });
        });
        return; // Skip normal processing for salgados
      } else {
        // Handle legacy salgados format (array of individual items)
        items.forEach((item: any) => {
          // 🛡️ SUPPLIER_ID FALLBACK: Try multiple sources for supplier_id
          const supplierId = item.supplier?.id || item.supplier_id || (item.supplier as any)?.supplier_id;
          const supplierName = item.supplier?.name || 'Fornecedor';
          
          if (!supplierId) {
            console.warn(`⚠️ [SUPPLIER_ID] Missing supplier_id for legacy salgados item:`, item);
            return;
          }
          
          // Add supplier to map
          if (!supplierMap.has(supplierId)) {
            supplierMap.set(supplierId, {
              id: supplierId,
              name: supplierName,
              logo_url: item.supplier?.logo_url,
              contact_email: item.supplier?.contact_email,
              contact_phone: item.supplier?.contact_phone,
              items: []
            });
          }
          
          const supplier = supplierMap.get(supplierId)!;
          
          // Process individual salgados item
           const optimizedItem: OptimizedItem = {
             id: item.id,
             name: item.name || item.product_name || item.product || 'Item sem nome',
             totalPrice: formatMonetaryValue(item.totalPrice || item.price || 0),
             category: 'salgados',
             quantity: item.quantity || 1,
             image_url: item.image_url || item.image || item.imageUrl,
             description: item.description,
             basePrice: formatMonetaryValue(item.basePrice || item.price || 0),
             pricePerPerson: formatMonetaryValue(item.pricePerPerson || 0),
             additional_items: Array.isArray(item.additionalItems) 
               ? item.additionalItems.join(', ') 
               : item.additional_items,
             necessary_items: Array.isArray(item.necessaryItems)
               ? item.necessaryItems.join(', ')
               : item.necessary_items,
             variants: item.variants,
             selectedVariant: item.selectedVariant,
             specialPricing: item.specialPricing,
             specialPrice: item.specialPrice === null || item.specialPrice === undefined ? null : formatMonetaryValue(item.specialPrice),
             priceAdditionalHour: formatMonetaryValueAsString(item.priceAdditionalHour),
             event_hour_time: item.event_hour_time,
           };
           
           // Preserve cake fields if this is a cake item
           const isCakeItem = item.type === 'bolo' || item.flavor || item.filling;
           if (isCakeItem) {
             optimizedItem.flavor = item.flavor;
             optimizedItem.colors = item.colors;
             optimizedItem.details = item.details;
             optimizedItem.filling = item.filling;
             optimizedItem.filling2 = item.filling2;
           }
          
          supplier.items.push(optimizedItem);
        });
        return; // Skip normal processing for salgados
      }
    }
    
    if (!items || !Array.isArray(items)) return;

    items.forEach((item: any) => {
      const supplierId = item.supplier?.id || item.supplier_id;
      const supplierName = item.supplier?.name || 'Fornecedor';

      if (!supplierId) return;

      // Get or create supplier entry
      if (!supplierMap.has(supplierId)) {
        supplierMap.set(supplierId, {
          id: supplierId,
          name: supplierName,
          logo_url: item.supplier?.logo_url,
          contact_email: item.supplier?.contact_email,
          contact_phone: item.supplier?.contact_phone,
          items: []
        });
      }

      const supplier = supplierMap.get(supplierId)!;

      // Create optimized item with category-specific fields
      const optimizedItem: OptimizedItem = {
        id: item.id,
        name: item.name || item.product_name || item.product,
        totalPrice: formatMonetaryValue(item.totalPrice),
        category: category,
        quantity: item.quantity || 1,
        image_url: item.image_url || item.image || item.imageUrl,
        description: item.description,
        basePrice: formatMonetaryValue(item.basePrice),
      };

      // Add salgados/buffet specific fields
      if (category === 'salgados' || category === 'buffet') {
        optimizedItem.pricePerPerson = formatMonetaryValue(item.pricePerPerson);
        optimizedItem.additional_items = Array.isArray(item.additionalItems) 
          ? item.additionalItems.join(', ') 
          : item.additional_items;
        optimizedItem.necessary_items = Array.isArray(item.necessaryItems)
          ? item.necessaryItems.join(', ')
          : item.necessary_items;
        optimizedItem.variants = item.variants;
        optimizedItem.selectedVariant = item.selectedVariant;
        optimizedItem.specialPricing = item.specialPricing;
        optimizedItem.specialPrice = item.specialPrice === null || item.specialPrice === undefined ? null : formatMonetaryValue(item.specialPrice);
        optimizedItem.priceAdditionalHour = formatMonetaryValueAsString(item.priceAdditionalHour);
        optimizedItem.event_hour_time = item.event_hour_time;
      }

      // Add decoration/disposable/candy specific fields
      if (['decoracao', 'descartaveis', 'doces', 'decoracao_descartaveis'].includes(category)) {
        optimizedItem.color = item.color;
        optimizedItem.material = item.material;
        optimizedItem.size = item.size;
        optimizedItem.units = item.units;
        optimizedItem.product_code = item.product_code;
        optimizedItem.sub_product = item.sub_product;
        optimizedItem.seller = item.seller;
        optimizedItem.product = item.product;
        optimizedItem.product_name = item.product_name;
      }

      // Add cake specific fields - detectar se é bolo
      const isCakeItem = category === 'bolos' || 
                         (category === 'doces' && item.type === 'bolo') ||
                         item.flavor || item.filling || item.filling2;
      
      if (isCakeItem) {
        optimizedItem.flavor = item.flavor;
        optimizedItem.colors = item.colors;
        optimizedItem.details = item.details;
        optimizedItem.filling = item.filling;
        optimizedItem.filling2 = item.filling2;
      }

      supplier.items.push(optimizedItem);
    });
  });

  // Convert Map to object with supplier IDs as keys
  const result: OptimizedSelectedSuppliers = {};
  supplierMap.forEach((supplier, supplierId) => {
    result[supplierId] = supplier;
  });

  return result;
}

/**
 * Normalizes selected_suppliers data to work with both optimized and legacy structures
 */
export function normalizeSelectedSuppliers(data: any): LegacySelectedSuppliers {
  if (!data) return {};

  const normalized: LegacySelectedSuppliers = {};

  // Check if data is in new optimized format (supplier IDs as keys)
  const isNewFormat = Object.keys(data).some(key => 
    key.length === 36 && key.includes('-') && data[key].items
  );

  if (isNewFormat) {
    // Convert new optimized format to legacy format
    Object.values(data).forEach((supplier: any) => {
      if (!supplier.items) return;

      supplier.items.forEach((item: OptimizedItem) => {
        const category = item.category || 'unknown';
        
        if (!normalized[category]) {
          normalized[category] = [];
        }

        const legacyItem: any = {
          id: item.id,
          name: item.name,
          category: category,
          totalPrice: formatMonetaryValue(item.totalPrice),
          quantity: item.quantity,
          image_url: item.image_url,
          imageUrl: item.image_url,
          image: item.image_url,
          description: item.description,
          basePrice: formatMonetaryValue(item.basePrice),
          supplier: {
            id: supplier.id,
            name: supplier.name,
            logo_url: supplier.logo_url,
            contact_email: supplier.contact_email,
            contact_phone: supplier.contact_phone
          },
          supplier_id: supplier.id
        };

        // Add category-specific fields
        if (category === 'salgados' || category === 'buffet') {
          legacyItem.pricePerPerson = formatMonetaryValue(item.pricePerPerson);
          legacyItem.additional_items = item.additional_items;
          legacyItem.necessary_items = item.necessary_items;
          legacyItem.additionalItems = item.additional_items?.split(', ') || [];
          legacyItem.necessaryItems = item.necessary_items?.split(', ') || [];
          legacyItem.variants = item.variants;
          legacyItem.selectedVariant = item.selectedVariant;
          legacyItem.specialPricing = item.specialPricing;
          legacyItem.specialPrice = item.specialPrice === null || item.specialPrice === undefined ? null : formatMonetaryValue(item.specialPrice);
          legacyItem.priceAdditionalHour = formatMonetaryValueAsString(item.priceAdditionalHour);
          legacyItem.event_hour_time = item.event_hour_time;
        }

        if (['decoracao', 'descartaveis', 'doces', 'decoracao_descartaveis'].includes(category)) {
          legacyItem.color = item.color;
          legacyItem.material = item.material;
          legacyItem.size = item.size;
          legacyItem.units = item.units;
          legacyItem.product_code = item.product_code;
          legacyItem.sub_product = item.sub_product;
          legacyItem.seller = item.seller;
          legacyItem.product = item.product;
          legacyItem.product_name = item.product_name;
        }

        // Restore cake specific fields
        const isCakeItem = category === 'bolos' || 
                           item.flavor || item.filling || item.filling2;
        
        if (isCakeItem) {
          legacyItem.flavor = item.flavor;
          legacyItem.colors = item.colors;
          legacyItem.details = item.details;
          legacyItem.filling = item.filling;
          legacyItem.filling2 = item.filling2;
        }

        normalized[category].push(legacyItem);
      });
    });
  } else {
    // Handle legacy format
    Object.entries(data).forEach(([category, items]: [string, any]) => {
      if (category === 'salgados') {
        if (Array.isArray(items)) {
          // Check if it's already in legacy format (array of items)
          if (items.length > 0 && items[0].category === 'salgados') {
            normalized.salgados = items;
          } else {
            // It's optimized format (array of suppliers), convert to legacy
            const legacyItems: LegacySalgadosItem[] = [];
            
            items.forEach((supplier: OptimizedSalgadosSupplier) => {
              supplier.items.forEach(item => {
              legacyItems.push({
                id: item.id,
                name: item.name,
                category: 'salgados',
                totalPrice: formatMonetaryValue(item.totalPrice),
                pricePerPerson: formatMonetaryValue(item.pricePerPerson),
                basePrice: formatMonetaryValue(item.basePrice),
                description: item.description,
                image_url: item.image_url,
                imageUrl: item.image_url,
                image: item.image_url,
                additional_items: item.additional_items,
                necessary_items: item.necessary_items,
                additionalItems: item.additional_items?.split(', ') || [],
                necessaryItems: item.necessary_items?.split(', ') || [],
                quantity: item.quantity,
                variants: item.variants,
                selectedVariant: item.selectedVariant,
                specialPricing: item.specialPricing,
                specialPrice: item.specialPrice === null || item.specialPrice === undefined ? null : formatMonetaryValue(item.specialPrice),
                priceAdditionalHour: formatMonetaryValueAsString(item.priceAdditionalHour),
                event_hour_time: item.event_hour_time,
                supplier: {
                  id: supplier.supplier_id, // Usar supplier_id como id para compatibilidade
                  name: supplier.name,
                  logo_url: supplier.logo_url,
                  contact_email: supplier.contact_email,
                  contact_phone: supplier.contact_phone
                },
                supplier_id: supplier.supplier_id // Para compatibilidade
              });
              });
            });
            
            normalized.salgados = legacyItems;
          }
        } else {
          normalized.salgados = [];
        }
      } else {
        // Standardize supplier_id for other categories
        if (Array.isArray(items)) {
          normalized[category] = items.map((item: any) => ({
            ...item,
            supplier_id: item.supplier_id || item.supplier?.id
          }));
        } else {
          normalized[category] = items;
        }
      }
    });
  }

  return normalized;
}

/**
 * Gets supplier information from optimized structure for a specific item
 */
export function getSupplierFromOptimized(
  data: OptimizedSelectedSuppliers | LegacyOptimizedSelectedSuppliers, 
  category: string, 
  itemId: string
): { id: string; name: string } | null {
  // Check new optimized format (supplier IDs as keys)
  if (data && typeof data === 'object') {
    for (const [supplierId, supplier] of Object.entries(data)) {
      if (supplier && supplier.items && Array.isArray(supplier.items)) {
        const item = supplier.items.find(item => item.id === itemId);
        if (item) {
          return { id: supplier.id, name: supplier.name };
        }
      }
    }
  }

  // Fallback: check legacy format for salgados
  if (category === 'salgados' && data && (data as any).salgados && Array.isArray((data as any).salgados)) {
    for (const supplier of (data as any).salgados) {
      const item = supplier.items?.find(item => item.id === itemId);
      if (item) {
        return { id: supplier.id, name: supplier.name };
      }
    }
  }
  
  return null;
}

/**
 * Checks if data is in new optimized format
 */
export function isOptimizedFormat(data: any): boolean {
  if (!data || typeof data !== 'object') return false;
  
  // New format has supplier UUIDs as keys with items arrays
  return Object.keys(data).some(key => 
    key.length === 36 && key.includes('-') && data[key].items
  );
}

/**
 * Checks if data is in legacy optimized format (for salgados)
 */
export function isLegacyOptimizedFormat(data: any, category: string): boolean {
  if (category === 'salgados' && Array.isArray(data[category])) {
    const firstItem = data[category][0];
    // Legacy optimized format has 'items' array, pure legacy format has 'category' field
    return firstItem && Array.isArray(firstItem.items) && !firstItem.category;
  }
  return false;
}
